import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { CartManagementMode } from '@flexyoo/api/models';
import { WhiteLabelService } from '@flexyoo/common';
import { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import {
  EmployeeSelectedBenefitsActions,
  employeeSelectedBenefitsSelectors,
} from '@flexyoo/employee/store';
import { shouldShowNetSavingColumn } from '@flexyoo/employee/util';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs';
import { PopUpService } from 'src/app/libs/shared/components/pop-up/pop-up.service';
import {
  EmployeeFinancialImpactReportComponent,
  NET_SIMULATION_DIALOG_INITIAL_WIDTH,
} from '../../employee-financial-impact-report/employee-financial-impact-report.component';
import { EmployeeBasketBenefitRowComponent } from '../employee-basket-benefit-row/employee-basket-benefit-row.component';

@Component({
  selector: 'flexyoo-employee-basket-cart',
  templateUrl: './employee-basket-cart.component.html',
  styleUrl: './employee-basket-cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    EmployeeBasketBenefitRowComponent,
  ],
})
export class EmployeeBasketCartComponent {
  private readonly store = inject(Store);
  private readonly popUpService = inject(PopUpService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  readonly selectedBenefitId = input<number | null>(null);
  readonly removeBenefit = output<EmployeeSelectedBenefit>();
  readonly signBenefit = output<EmployeeSelectedBenefit>();

  protected readonly wls = inject(WhiteLabelService);
  protected readonly expanded = signal(true);

  protected readonly cartBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCartBenefits)
  );
  protected readonly showNetSavingColumn = computed(() =>
    shouldShowNetSavingColumn(this.cartBenefits() ?? [])
  );

  /**
   * Temporary (50857): the financial impact report is only offered to clients with
   * cart logic. This panel is shown to the other clients, so the button stays hidden
   * until the report is ready for them.
   */
  protected readonly isCartMode = toSignal(
    this.store
      .select(tenantSettingsSelectors.selectCartManagementMode)
      .pipe(map((mode) => mode === CartManagementMode.Cart)),
    { initialValue: false }
  );

  expandCart(): void {
    this.expanded.set(true);
  }

  protected onOpenFinancialImpactReport(): void {
    this.dialog.open(EmployeeFinancialImpactReportComponent, {
      width: NET_SIMULATION_DIALOG_INITIAL_WIDTH,
    });
  }

  protected onRemoveBenefit(benefit: EmployeeSelectedBenefit): void {
    this.popUpService
      .openConfirmDialog({
        title: 'employee.basket.benefit-deletion-popup.title',
        text: this.translate.instant('employee.basket.benefit-deletion-popup.text', {
          name: this.translate.instant(benefit.name),
        }),
        htmlContent: `<p style="text-align: center; display: block; max-width: 300px; margin: 20px auto 0;"><strong>${this.translate.instant(
          'employee.basket.benefit-deletion-popup.warning'
        )}</strong></p>`,
        textYes: 'general.conf',
        textNo: 'general.cancel',
        swapButtons: true,
        width: '520px',
      })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.store.dispatch(EmployeeSelectedBenefitsActions.removeBenefit({ benefit }));
        this.removeBenefit.emit(benefit);
      });
  }
}
